import { intBetween, floatBetween, pick, weighted, type Rng } from './seed'
import {
  CZECH_CITIES,
  RESTAURANT_NAME_PREFIXES,
  RESTAURANT_NAME_SUFFIXES,
  FIRST_NAMES_CZ,
  LAST_NAMES_CZ,
  INCIDENT_TITLES_CZ,
  TEAMS_FIXTURE,
  MODULE_DISTRIBUTION,
  MODULE_AVG_PRICE_CZK,
  COSTS_BREAKDOWN_KEYS,
} from './fixtures'
import type {
  DashboardSnapshot,
  Incident,
  ModuleRow,
  TeamRow,
  LocationRow,
  SalespersonRow,
  IncidentCategory,
  Severity,
  ModuleCode,
} from '../types'

const NOW = () => new Date()

export interface Scale {
  locations: number
  mrrBaselineCzk: number
  fixedMonthlyCzk: number
}

export function generateLocationName(rng: Rng): { name: string; city: string } {
  const prefix = pick(rng, RESTAURANT_NAME_PREFIXES)
  const suffix = pick(rng, RESTAURANT_NAME_SUFFIXES)
  const city = pick(rng, CZECH_CITIES)
  return { name: `${prefix} ${suffix}`, city }
}

export function generatePersonName(rng: Rng): string {
  return `${pick(rng, FIRST_NAMES_CZ)} ${pick(rng, LAST_NAMES_CZ)}`
}

export function generateModules(rng: Rng, scale: Scale): { modules: ModuleRow[]; mrr: number } {
  const modules: ModuleRow[] = []
  let mrr = 0
  for (const [code, share] of MODULE_DISTRIBUTION) {
    const activeInstalls = Math.round(scale.locations * share * floatBetween(rng, 0.92, 1.08))
    const avgPrice = MODULE_AVG_PRICE_CZK[code]
    const moduleMrr = Math.round(activeInstalls * avgPrice)
    modules.push({ code, activeInstalls, mrrCzk: moduleMrr })
    mrr += moduleMrr
  }
  const scaleFactor = scale.mrrBaselineCzk / Math.max(1, mrr)
  modules.forEach((m) => (m.mrrCzk = Math.round(m.mrrCzk * scaleFactor)))
  mrr = modules.reduce((s, m) => s + m.mrrCzk, 0)
  return { modules, mrr }
}

export function generateTopLocations(rng: Rng, total: number, count = 10): LocationRow[] {
  const rows: LocationRow[] = []
  for (let i = 0; i < count; i++) {
    const { name, city } = generateLocationName(rng)
    const mrrCzk = Math.round(floatBetween(rng, 8000, 22000) - i * 600)
    rows.push({
      id: `loc:${i}`,
      name,
      city,
      mrrCzk: Math.max(2000, mrrCzk),
    })
  }
  rows.sort((a, b) => b.mrrCzk - a.mrrCzk)
  void total
  return rows
}

export function generatePipeline(rng: Rng): SalespersonRow[] {
  const reps: SalespersonRow[] = []
  for (let i = 0; i < 5; i++) {
    reps.push({
      id: `rep:${i}`,
      name: generatePersonName(rng),
      dealsInPipeline: intBetween(rng, 4, 14),
      pipelineCzk: Math.round(floatBetween(rng, 80_000, 320_000)),
      closedThisMonth: intBetween(rng, 0, 4),
    })
  }
  reps.sort((a, b) => b.pipelineCzk - a.pipelineCzk)
  return reps
}

export function generateTeams(rng: Rng): TeamRow[] {
  return TEAMS_FIXTURE.map((t, i) => ({
    id: t.id,
    name: t.nameCz,
    members: t.members,
    ordersHandled: t.id === 'support' ? 0 : intBetween(rng, 12, 80),
    csat: Number((floatBetween(rng, 4.3, 4.8) + (t.id === 'support' ? 0.05 : 0)).toFixed(2)),
    incidentsResolved: t.id === 'support' ? intBetween(rng, 30, 60) : intBetween(rng, 0, 10),
    rank: i + 1,
  }))
}

export function generateIncident(rng: Rng, refDate = NOW()): Incident {
  const template = pick(rng, INCIDENT_TITLES_CZ)
  const ageMin = intBetween(rng, 5, 240)
  const severity: Severity = weighted(rng, [
    ['P3', 0.6],
    ['P2', 0.3],
    ['P1', 0.1],
  ])
  const { name, city } = generateLocationName(rng)
  return {
    id: `INC-${intBetween(rng, 100, 9999)}`,
    title: template.title,
    severity,
    status: weighted(rng, [
      ['open', 0.55],
      ['in-progress', 0.45],
    ]),
    openedAt: new Date(refDate.getTime() - ageMin * 60_000).toISOString(),
    location: `${city} — ${name}`,
    category: template.category,
    responsibleTeam: 'support',
  }
}

export function generateIncidents(rng: Rng, count: number, refDate = NOW()): Incident[] {
  const arr: Incident[] = []
  for (let i = 0; i < count; i++) arr.push(generateIncident(rng, refDate))
  arr.sort((a, b) => +new Date(b.openedAt) - +new Date(a.openedAt))
  return arr
}

export function generateCategoryBreakdown(
  rng: Rng,
  total: number,
): Array<{ category: IncidentCategory; count: number }> {
  const shares: Array<[IncidentCategory, number]> = [
    ['payment', 0.18],
    ['sync', 0.28],
    ['hardware', 0.22],
    ['training', 0.1],
    ['connectivity', 0.14],
    ['other', 0.08],
  ]
  return shares.map(([category, share]) => ({
    category,
    count: Math.max(0, Math.round(total * share * floatBetween(rng, 0.7, 1.3))),
  }))
}

export function generateRevenueHistory(rng: Rng, baseline: number, days = 30) {
  const arr: Array<{ t: string; v: number }> = []
  const start = new Date(Date.now() - days * 86_400_000)
  let val = baseline * 0.92
  for (let i = 0; i < days; i++) {
    const t = new Date(start.getTime() + i * 86_400_000)
    val += baseline * floatBetween(rng, -0.005, 0.012)
    arr.push({ t: t.toISOString(), v: Math.round(val) })
  }
  return arr
}

export function generateIncidentHistory(rng: Rng, days = 14) {
  const arr: Array<{ t: string; open: number; closed: number }> = []
  const start = new Date(Date.now() - days * 86_400_000)
  let open = intBetween(rng, 4, 10)
  for (let i = 0; i < days; i++) {
    const t = new Date(start.getTime() + i * 86_400_000)
    const opened = intBetween(rng, 2, 10)
    const closed = intBetween(rng, 2, 11)
    open = Math.max(2, open + opened - closed)
    arr.push({ t: t.toISOString(), open, closed })
  }
  return arr
}

export function generateChurn6m(rng: Rng) {
  const arr: Array<{ t: string; gained: number; lost: number }> = []
  const now = new Date()
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    arr.push({
      t: d.toISOString(),
      gained: intBetween(rng, 8, 22),
      lost: intBetween(rng, 2, 8),
    })
  }
  return arr
}

export function generateMargin30d(rng: Rng) {
  const arr: Array<{ t: string; v: number }> = []
  const start = new Date(Date.now() - 30 * 86_400_000)
  let v = floatBetween(rng, 0.42, 0.46)
  for (let i = 0; i < 30; i++) {
    v += floatBetween(rng, -0.01, 0.012)
    v = Math.max(0.3, Math.min(0.62, v))
    arr.push({
      t: new Date(start.getTime() + i * 86_400_000).toISOString(),
      v: Number(v.toFixed(3)),
    })
  }
  return arr
}

export function generateCsatHistory(rng: Rng, days = 30) {
  const arr: Array<{ t: string; v: number }> = []
  const start = new Date(Date.now() - days * 86_400_000)
  let v = 4.5
  for (let i = 0; i < days; i++) {
    v += floatBetween(rng, -0.04, 0.06)
    v = Math.max(3.9, Math.min(4.9, v))
    arr.push({
      t: new Date(start.getTime() + i * 86_400_000).toISOString(),
      v: Number(v.toFixed(2)),
    })
  }
  return arr
}

export function generateStoryOfTheDay(
  rng: Rng,
): NonNullable<DashboardSnapshot['incidents']['storyOfTheDay']> {
  const { name, city } = generateLocationName(rng)
  const stories = [
    'Pokladna vyřešena za 24 minut — bez vlivu na zákazníky',
    'Wolt sync sám obnoven po reboot routeru, support proaktivně volal',
    'Karty zase běží — terminál restart vyřešil',
    'Tiskárna v baru — papírek doplněn, ale support už mluvil s majitelem o dotokování',
  ]
  return {
    title: pick(rng, stories),
    closedAt: new Date(Date.now() - intBetween(rng, 15, 90) * 60_000).toISOString(),
    locationName: `${city} — ${name}`,
  }
}

export function generateStarOfTheWeek(rng: Rng) {
  return {
    name: generatePersonName(rng),
    team: pick(rng, TEAMS_FIXTURE).nameCz,
    reason: pick(rng, [
      'Vyřešila 14 incidentů za týden bez jediného opakování',
      'Onboardoval 4 nové pobočky bez chyby',
      'Zachránil platby na akci s 800 hosty',
      'Tým u něj poprvé skončil týden s P1 = 0',
    ]),
  }
}

export function buildInitialSnapshot(rng: Rng, scale: Scale): DashboardSnapshot {
  const refDate = NOW()
  const { modules, mrr } = generateModules(rng, scale)
  const targetCzk = Math.round(scale.mrrBaselineCzk * floatBetween(rng, 1.06, 1.15))
  const mrrDeltaPct = Number(floatBetween(rng, 1.5, 5.8).toFixed(2))

  const openIncidents = intBetween(rng, 4, 10)
  const incidentList = generateIncidents(rng, openIncidents, refDate)

  const fixedMonthly = scale.fixedMonthlyCzk
  const grossMargin = Number(floatBetween(rng, 0.42, 0.48).toFixed(3))

  const teams = generateTeams(rng)

  return {
    asOf: refDate.toISOString(),
    clients: {
      active: scale.locations,
      newThisMonth: intBetween(rng, 4, 12),
      newToday: intBetween(rng, 0, 2),
      churnedThisMonth: intBetween(rng, 2, 7),
      total: scale.locations,
      active7d: Math.round(scale.locations * 0.6),
      trial: null,
      newLast14d: 0, inactive7d: 0, wentQuiet7d: 0,
    },
    revenue: {
      unknownDirectionCount: 0,
      unknownDirectionCzk: 0,
      mrrCzk: mrr,
      mrrDeltaPct,
      targetCzk,
      arpuCzk: Math.round(mrr / scale.locations),
      ltvCzk: Math.round((mrr / scale.locations) * 22),
      cashflowMonthCzk: Math.round(mrr - fixedMonthly),
      grossMarginPct: grossMargin * 100,
      byModule: modules.map<{ moduleCode: ModuleCode; mrrCzk: number }>((m) => ({
        moduleCode: m.code,
        mrrCzk: m.mrrCzk,
      })),
    },
    churn: {
      ratePct: Number(floatBetween(rng, 1.2, 2.4).toFixed(2)),
      lostThisMonth: intBetween(rng, 2, 7),
    },
    costs: {
      fixedMonthlyCzk: fixedMonthly,
      categories: COSTS_BREAKDOWN_KEYS.map((c) => ({
        key: c.key,
        labelKey: c.labelKey,
        amountCzk: Math.round(fixedMonthly * c.share),
      })),
    },
    incidents: {
      open: openIncidents,
      closedToday: intBetween(rng, 6, 18),
      closedTotal: intBetween(rng, 80, 600),
      mttrMin: intBetween(rng, 45, 110),
      daysWithoutP1: intBetween(rng, 0, 21),
      avgResponseMin: intBetween(rng, 2, 6),
      list: incidentList,
      categoryBreakdown: generateCategoryBreakdown(rng, openIncidents * 6),
      p95ResponseMin: intBetween(rng, 8, 18),
      storyOfTheDay: generateStoryOfTheDay(rng),
    },
    support: {
      csat: Number(floatBetween(rng, 4.4, 4.7).toFixed(2)),
      nps: intBetween(rng, 44, 62),
      ticketsToday: intBetween(rng, 28, 64),
      csatHistory: generateCsatHistory(rng),
    },
    teams,
    starOfTheWeek: generateStarOfTheWeek(rng),
    modules,
    topLocations: generateTopLocations(rng, scale.locations),
    pipeline: generatePipeline(rng),
    onboarding: {
      startedThisMonth: intBetween(rng, 20, 60),
      succeededThisMonth: intBetween(rng, 15, 50),
      failedThisMonth: intBetween(rng, 1, 10),
    },
    serviceHealth: [
      { name: 'DELIVERY_HERO', status: 'ok', avgLatencyMs: intBetween(rng, 300, 900), callsLast24h: intBetween(rng, 50, 150) },
      { name: 'BOLT_FOOD', status: 'ok', avgLatencyMs: intBetween(rng, 200, 600), callsLast24h: intBetween(rng, 20, 80) },
    ],
    venueMap: [
      { id: 'v1', name: 'Vinohrady', city: 'Vinohrady', lat: 50.075, lng: 14.437, status: 'active', active7d: true, everActive: true },
      { id: 'v2', name: 'Uhříněves', city: 'Uhříněves', lat: 50.023, lng: 14.596, status: 'active', active7d: true, everActive: true },
      { id: 'v3', name: 'Bowling Bar 18', city: 'Hradec Králové', lat: 50.203, lng: 15.833, status: 'active', active7d: true, everActive: true },
      { id: 'v4', name: 'Mohu sushi Kunovice', city: 'Kunovice', lat: 49.04, lng: 17.47, status: 'active', active7d: true, everActive: true },
      {
        id: 'v5',
        name: 'Pizzerie Tara - Uherské hradiště',
        city: 'Uherské Hradiště',
        lat: 49.07,
        lng: 17.461,
        status: 'active',
        active7d: true,
        everActive: true,
      },
      {
        id: 'v6',
        name: 'Pizza House - Nové Strašecí',
        city: 'Nové Strašecí',
        lat: 50.155,
        lng: 13.907,
        status: 'active',
        active7d: true,
        everActive: true,
      },
    ],
    serviceAdoption: [
      { name: 'E-shop', venues: 6 },
      { name: 'Objednáme', venues: 5 },
      { name: 'Aplikace', venues: 4 },
    ],
    syncRatio: { success: 1197, error: 451, pending: 916, errorPct: 27.4 },
    recentActivity: [],
    venueMapUnplaced: 0,
    venueDirectory: {},
    ordersMonth: { total: 0, byVenue: [] },
    ordersToday: [
      {
        id: 'v6',
        name: 'Pizza House - Nové Strašecí',
        city: 'Nové Strašecí',
        lat: 50.155,
        lng: 13.907,
        orders: intBetween(rng, 12, 40),
        lastAt: NOW().toISOString(),
      },
      {
        id: 'v2',
        name: 'Uhříněves',
        city: 'Uhříněves',
        lat: 50.023,
        lng: 14.596,
        orders: intBetween(rng, 8, 30),
        lastAt: NOW().toISOString(),
      },
      {
        id: 'v1',
        name: 'Vinohrady',
        city: 'Vinohrady',
        lat: 50.075,
        lng: 14.437,
        orders: intBetween(rng, 3, 18),
        lastAt: NOW().toISOString(),
      },
    ],
    history: {
      mrr30d: generateRevenueHistory(rng, mrr),
      clients30d: [],
      revenueMonthly: [],
      incidents14d: generateIncidentHistory(rng),
      churn6m: generateChurn6m(rng),
      margin30d: generateMargin30d(rng),
    },
  }
}

export function buildZeroStateSnapshot(): DashboardSnapshot {
  const refDate = NOW()
  return {
    asOf: refDate.toISOString(),
    clients: {
      active: 0,
      newThisMonth: 0,
      newToday: 0,
      churnedThisMonth: 0,
      total: 0,
      active7d: 0,
      trial: null,
      newLast14d: 0, inactive7d: 0, wentQuiet7d: 0,
    },
    revenue: {
      mrrCzk: 0,
      mrrDeltaPct: 0,
      targetCzk: 0,
      arpuCzk: 0,
      ltvCzk: 0,
      cashflowMonthCzk: 0,
      grossMarginPct: 0,
      byModule: [], unknownDirectionCount: 0, unknownDirectionCzk: 0,
    },
    churn: { ratePct: 0, lostThisMonth: 0 },
    costs: { fixedMonthlyCzk: 0, categories: [] },
    incidents: {
      open: 0,
      closedToday: 0,
      closedTotal: 0,
      mttrMin: 0,
      daysWithoutP1: 0,
      avgResponseMin: 0,
      list: [],
      categoryBreakdown: [],
      p95ResponseMin: 0,
      storyOfTheDay: null,
    },
    support: { csat: 0, nps: 0, ticketsToday: 0, csatHistory: [] },
    teams: TEAMS_FIXTURE.map((t, i) => ({
      id: t.id,
      name: t.nameCz,
      members: 0,
      ordersHandled: 0,
      csat: 0,
      incidentsResolved: 0,
      rank: i + 1,
    })),
    starOfTheWeek: null,
    modules: [],
    topLocations: [],
    pipeline: [],
    onboarding: { startedThisMonth: 0, succeededThisMonth: 0, failedThisMonth: 0 },
    serviceHealth: [],
    venueMap: [],
    venueMapUnplaced: 0,
    venueDirectory: {},
    ordersToday: [],
    ordersMonth: { total: 0, byVenue: [] },
    serviceAdoption: [],
    syncRatio: { success: 0, error: 0, pending: 0, errorPct: null },
    recentActivity: [],
    history: { mrr30d: [], clients30d: [], revenueMonthly: [], incidents14d: [], churn6m: [], margin30d: [] },
  }
}
